import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useClosePlayerModal } from "@/hooks/playerModal";
import { ExternalLinkIcon, Loader2Icon, XIcon } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { banDurationToShortString, banDurationToString, cn } from "@/lib/utils";
import { Link, useLocation } from "wouter";
import type { SanctionTemplateType } from "@shared/otherTypes";
import { sortSanctionTemplates } from "@shared/utils";
import { HoverTip } from '@/components/ui/tooltip';
import { useAdminPerms } from "@/hooks/auth";

// Consts
const reasonTruncateLength = 150;
const ADD_NEW_SELECT_OPTION = '!add-new';
const defaultDurations = ['permanent', '2 hours', '8 hours', '1 day', '2 days', '1 week', '2 weeks'];
const minuteDurations = ['15 minutes', '30 minutes'];

// Types
type BanFormRespType = {
    reason: string;
    duration: string;
}
export type BanFormType = HTMLDivElement & {
    focusReason: () => void;
    clearData: () => void;
    getData: () => BanFormRespType;
}
type BanFormProps = {
    banTemplates?: SanctionTemplateType[]; //undefined = loading
    disabled?: boolean;
    onNavigateAway?: () => void;
    templatesPath?: string;
    templatesLabel?: string;
    allowMinutes?: boolean; //admin jails can be set in minutes, bans cannot
    onTemplateSelect?: (template: SanctionTemplateType) => void;
};

/**
 * A form to set ban reason and duration.
 */
export default forwardRef(function BanForm({
    banTemplates,
    disabled,
    onNavigateAway,
    templatesPath = '/settings/ban-templates',
    templatesLabel = 'Add Ban Template',
    allowMinutes = false,
    onTemplateSelect,
}: BanFormProps, ref) {
    const { hasPerm } = useAdminPerms();
    const canAnticheat = hasPerm('players.anticheat');
    const reasonRef = useRef<HTMLInputElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const customMultiplierRef = useRef<HTMLInputElement>(null);
    //inside a modal the list has to be portaled into the dialog, or the modal's
    //scroll lock cancels the wheel events and the template list cannot be scrolled
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
    //and it may not grow past the tab area, which ends at the footer separator
    const [collisionBoundary, setCollisionBoundary] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setPortalContainer(anchorRef.current?.closest('[role="dialog"]') as HTMLElement ?? null);
        setCollisionBoundary(anchorRef.current?.closest('[data-modal-content]') as HTMLElement ?? null);
    }, []);
    const setLocation = useLocation()[1];
    const [currentDuration, setCurrentDuration] = useState('2 days');
    const [customUnits, setCustomUnits] = useState('days');
    const [reasonText, setReasonText] = useState('');
    const [isTemplateListOpen, setTemplateListOpen] = useState(false);
    const closeModal = useClosePlayerModal();

    //Exposing methods to the parent
    useImperativeHandle(ref, () => {
        return {
            getData: () => {
                return {
                    reason: reasonRef.current?.value.trim(),
                    duration: currentDuration === 'custom'
                        ? `${customMultiplierRef.current?.value} ${customUnits}`
                        : currentDuration,
                };
            },
            clearData: () => {
                if (!reasonRef.current || !customMultiplierRef.current) return;
                reasonRef.current.value = '';
                customMultiplierRef.current.value = '';
                setReasonText('');
                setTemplateListOpen(false);
                setCurrentDuration('2 days');
                setCustomUnits('days');
            },
            focusReason: () => {
                reasonRef.current?.focus();
            }
        };
    }, [reasonRef, customMultiplierRef, currentDuration, customUnits]);

    const handleTemplateSelectChange = (value: string) => {
        if (value === ADD_NEW_SELECT_OPTION) {
            setLocation(templatesPath);
            onNavigateAway?.();
        } else {
            if (!banTemplates) return;
            const template = banTemplates.find(template => template.id === value);
            if (!template) return;

            const processedDuration = banDurationToString(template.duration);
            const knownDurations = allowMinutes
                ? [...minuteDurations, ...defaultDurations]
                : defaultDurations;
            if (knownDurations.includes(processedDuration)) {
                setCurrentDuration(processedDuration);
            } else if (typeof template.duration === 'object') {
                setCurrentDuration('custom');
                customMultiplierRef.current!.value = template.duration.value.toString();
                setCustomUnits(template.duration.unit);
            }

            reasonRef.current!.value = template.reason;
            setReasonText(template.reason);
            setTemplateListOpen(false);
            onTemplateSelect?.(template);
            setTimeout(() => {
                reasonRef.current!.focus();
            }, 50);
        }
    }

    //anticheat templates would just be rejected by the backend, so they are
    //never offered to admins that cannot use them
    const allowedTemplates = useMemo(() => {
        if (!banTemplates) return;
        return canAnticheat ? banTemplates : banTemplates.filter((tpl) => !tpl.anticheat);
    }, [banTemplates, canAnticheat]);

    const visibleTemplates = useMemo(() => {
        if (!allowedTemplates) return;
        const sorted = sortSanctionTemplates(allowedTemplates);
        const needle = reasonText.trim().toLowerCase();
        if (!needle) return sorted;
        return sorted.filter((template) => template.reason.toLowerCase().includes(needle));
    }, [allowedTemplates, reasonText]);

    const clearReason = () => {
        if (!reasonRef.current) return;
        reasonRef.current.value = '';
        setReasonText('');
        reasonRef.current.focus();
    };

    // Simplifying the jsx below
    let banTemplatesContentNode: React.ReactNode;
    if (!Array.isArray(banTemplates) || !allowedTemplates || !visibleTemplates) {
        banTemplatesContentNode = (
            <div className="text-secondary-foreground text-center p-4">
                <Loader2Icon className="inline animate-spin size-6" />
            </div>
        );
    } else if (!allowedTemplates.length) {
        banTemplatesContentNode = (
            <div className="text-warning-inline text-center p-4">
                You do not have any template configured. <br />
                <Link
                    href={templatesPath}
                    className="cursor-pointer underline hover:text-accent"
                    onClick={() => { closeModal(); }}
                >
                    {templatesLabel}
                    <ExternalLinkIcon className="inline mr-1 h-4" />
                </Link>
            </div>
        );
    } else if (!visibleTemplates.length) {
        banTemplatesContentNode = (
            <div className="text-muted-foreground text-center text-sm p-3">
                No template matches what you are typing.
            </div>
        );
    } else {
        banTemplatesContentNode = <>
            {visibleTemplates.map((template) => {
                const duration = banDurationToShortString(template.duration);
                const reason = template.reason.length > reasonTruncateLength
                    ? template.reason.slice(0, reasonTruncateLength - 3) + '...'
                    : template.reason;
                return (
                    <button
                        key={template.id}
                        type="button"
                        onClick={() => handleTemplateSelectChange(template.id)}
                        className={cn(
                            'w-full text-left rounded-bar px-2 py-1.5 text-sm',
                            'hover:bg-secondary hover:text-secondary-foreground',
                        )}
                    >
                        <span className="inline-block pr-1.5 font-[monospace] font-medium opacity-75">{duration}</span>
                        {template.anticheat ? (
                            <span className="inline-block pr-1.5 font-[monospace] font-medium text-anticheat">AC</span>
                        ) : null}
                        {reason}
                    </button>
                );
            })}
            <button
                type="button"
                onClick={() => handleTemplateSelectChange(ADD_NEW_SELECT_OPTION)}
                className="w-full text-left rounded-bar px-2 py-1.5 text-sm font-bold text-warning-inline hover:bg-secondary"
            >
                {templatesLabel}
                <ExternalLinkIcon className="inline ml-1 h-4" />
            </button>
        </>;
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
                <Label htmlFor="banReason">
                    Reason
                </Label>
                <Popover
                    open={isTemplateListOpen && !disabled}
                    onOpenChange={setTemplateListOpen}
                >
                    <PopoverAnchor asChild>
                        <div className="relative" ref={anchorRef}>
                            <Input
                                id="banReason"
                                ref={reasonRef}
                                placeholder="The reason for the ban, rule violated, etc."
                                className="w-full pr-9"
                                disabled={disabled}
                                autoFocus
                                autoComplete="off"
                                onChange={(e) => {
                                    setReasonText(e.target.value);
                                    setTemplateListOpen(true);
                                }}
                                onClick={() => setTemplateListOpen(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') setTemplateListOpen(false);
                                }}
                            />
                            {!!reasonText && (
                                <HoverTip label="Clear reason">
                                    <button
                                        type="button"
                                        aria-label="Clear reason"
                                        onClick={clearReason}
                                        className={cn(
                                            'absolute right-[13px] top-1/2 -translate-y-1/2',
                                            'grid place-items-center size-5',
                                            'text-faint transition-colors hover:text-accent',
                                        )}
                                    >
                                        <XIcon className="size-5" />
                                    </button>
                                </HoverTip>
                            )}
                        </div>
                    </PopoverAnchor>
                    <PopoverContent
                        align="start"
                        sideOffset={4}
                        collisionPadding={0}
                        collisionBoundary={collisionBoundary ?? undefined}
                        container={portalContainer}
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        className={cn(
                            'p-1 w-[var(--radix-popover-trigger-width)] tracking-wide',
                            //inside a modal the list runs down to the tab area edge, ie the
                            //footer separator - the 24rem only bounds it outside of one
                            'max-h-[min(24rem,var(--radix-popover-content-available-height))]',
                            'overflow-y-auto overscroll-contain',
                        )}
                    >
                        {banTemplatesContentNode}
                    </PopoverContent>
                </Popover>
            </div>
            <div className="flex flex-col gap-3">
                <Label htmlFor="durationSelect">
                    Duration
                </Label>
                <div className="space-y-1">
                    <Select
                        onValueChange={setCurrentDuration}
                        value={currentDuration}
                        disabled={disabled}
                    >
                        <SelectTrigger id="durationSelect" className="tracking-wide">
                            <SelectValue placeholder="Select Duration" />
                        </SelectTrigger>
                        <SelectContent className="tracking-wide">
                            {allowMinutes && <SelectItem value="15 minutes">15 MINUTES</SelectItem>}
                            {allowMinutes && <SelectItem value="30 minutes">30 MINUTES</SelectItem>}
                            <SelectItem value="2 hours">2 HOURS</SelectItem>
                            <SelectItem value="8 hours">8 HOURS</SelectItem>
                            <SelectItem value="1 day">1 DAY</SelectItem>
                            <SelectItem value="2 days">2 DAYS</SelectItem>
                            <SelectItem value="1 week">1 WEEK</SelectItem>
                            <SelectItem value="2 weeks">2 WEEKS</SelectItem>
                            <SelectItem value="permanent" className="font-bold">PERMANENT</SelectItem>
                            <SelectItem value="custom" className="font-bold">CUSTOM</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="flex flex-row gap-2">
                        <Input
                            id="durationMultiplier"
                            type="number"
                            placeholder="1"
                            required
                            disabled={currentDuration !== 'custom' || disabled}
                            ref={customMultiplierRef}
                        />
                        <Select
                            onValueChange={setCustomUnits}
                            value={customUnits}
                        >
                            <SelectTrigger
                                className="tracking-wide"
                                id="durationUnits"
                                disabled={currentDuration !== 'custom' || disabled}
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="tracking-wide">
                                {allowMinutes && <SelectItem value="minutes">MINUTES</SelectItem>}
                                <SelectItem value="hours">HOURS</SelectItem>
                                <SelectItem value="days">DAYS</SelectItem>
                                <SelectItem value="weeks">WEEKS</SelectItem>
                                <SelectItem value="months">MONTHS</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>
        </div>
    );
});
